import type {FilterQuery} from '@github-ui/filter-core/filter-query'
import {type AnyBlock, BlockType, FilterOperator} from '@github-ui/filter-core/types'
import {getUnescapedFilterValue} from '@github-ui/filter-core/utils'

const ISSUE = 1
const PULL_REQUEST = 2
const MIXED = ISSUE | PULL_REQUEST

export type IssueQueryKind = 'issue' | 'pull-request' | 'mixed'

export function issueQueryKind(query: FilterQuery): IssueQueryKind {
  const types = expressionTypes(query.blocks)
  if (types === ISSUE) return 'issue'
  if (types === PULL_REQUEST) return 'pull-request'
  return 'mixed'
}

function expressionTypes(blocks: readonly AnyBlock[]): number | undefined {
  let alternatives = 0
  let conjunction = MIXED
  let expectingOperand = true

  for (const block of blocks) {
    if (block.type === BlockType.Space) continue
    // The unfinished group owns the remaining tail, including any ORs inside it.
    if (block.type === BlockType.UnmatchedOpenParen) return alternatives | conjunction
    if (block.type === BlockType.Keyword && (block.raw === 'AND' || block.raw === 'OR')) {
      if (expectingOperand) return undefined
      if (block.raw === 'OR') {
        alternatives |= conjunction
        conjunction = MIXED
      }
      expectingOperand = true
      continue
    }

    const types = blockTypes(block)
    if (types === undefined) return undefined
    conjunction &= types
    expectingOperand = false
  }
  return alternatives | conjunction
}

function blockTypes(block: AnyBlock): number | undefined {
  if (block.type === BlockType.Group) return expressionTypes(block.blocks) ?? MIXED
  if (block.type === BlockType.UnmatchedCloseParen) return undefined
  if (block.type !== BlockType.Filter || !['is', 'type'].includes(block.provider.key)) return MIXED
  if (!block.value.values.length) return MIXED

  let types = 0
  for (const value of block.value.values) {
    switch (getUnescapedFilterValue(value.value)?.toLowerCase()) {
      case 'issue':
        types |= ISSUE
        break
      case 'pr':
      case 'pull-request':
        types |= PULL_REQUEST
        break
      default:
        return MIXED
    }
  }
  if (block.operator === FilterOperator.IsNot || block.operator === FilterOperator.IsNotOneOf) return MIXED ^ types
  if (block.operator === FilterOperator.Is || block.operator === FilterOperator.IsOneOf) return types
  return MIXED
}
